import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import DropdownMenu from './PricingDropdownMenu';
import ThemeIcons from '../../../../theme/Icons';
import Icon from '../../../misc/Icon';
import { isFormattedCorrectly, formatCellValue } from './pricingHelpers';

const SummaryRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: ${(props) => {
    if (props.isLast) {
      return 'none';
    }
    return `1px solid ${props.theme?.borderColor || '#edf2f7'}`;
  }};
  background-color: ${(props) => props.backgroundColor || props.theme?.backgroundColor || 'transparent'};

  &:hover {
    .pricing-table-dropdown-button {
      display: ${(props) => {
    if (props.canManageColumns) {
      return 'flex !important';
    }
    return 'none';
  }};
    }

    input{
      border: 1px solid ${(props) => props.borderColor || props.theme?.borderColor || '#e2e8f0'};
    }
  }
`;

const SummaryLabel = styled.span`
  font-size: ${(props) => {
    if (props.isBold) {
      return `${props.fontSize + 2}px`;
    } else if (props.fontSize) {
      return props.fontSize;
    }
    return '16px';
  }};
  color: ${(props) => props.fontColor || props.theme?.fontColorPrimary || '#4a5568'};
  font-weight: ${(props) => {
    if (props.isBold) {
      return '600';
    }
    return '400';
  }};
  font-style: ${(props) => props.fontStyle || 'normal'};
  text-align: ${(props) => props.textAlign || 'left'};
  text-transform: ${(props) => props.textTransform || 'none'};
  text-decoration: ${(props) => props.textDecoration || 'none'};
  padding: ${(props) => {
    if (props.padding) {
      return `${props.padding}px 8px`;
    }
    return '0 8px';
  }};
`;

const SummaryValue = styled.span`
  font-size: ${(props) => {
    if (props.fontSize) {
      return `${props.fontSize}px`;
    } else if (props.isBold) {
      return '1rem';
    }
    return '0.875rem';
  }};
  font-weight: ${(props) => {
    if (props.fontWeight) {
      return props.fontWeight;
    }
    if (props.isBold) {
      return '600';
    }
    return '400';
  }};
  color: ${(props) => props.fontColor || props.theme?.fontColorPrimary || '#2d3748'};
  font-style: ${(props) => props.fontStyle || 'normal'};
  text-align: ${(props) => props.textAlign || 'right'};
  text-transform: ${(props) => props.textTransform || 'none'};
  text-decoration: ${(props) => props.textDecoration || 'none'};
  padding: ${(props) => {
    if (props.padding) {
      return `${props.padding}px 8px`;
    }
    return '0 8px';
  }};
`;

const SummaryInput = styled.input`
  font-size: ${(props) => {
    if (props.fontSize) {
      return `${props.fontSize}px`;
    }
    return '0.875rem';
  }};
  font-weight: ${(props) => props.fontWeight || 'normal'};
  color: ${(props) => props.fontColor || props.theme?.fontColorPrimary || 'inherit'};
  font-style: ${(props) => props.fontStyle || 'normal'};
  padding: 0.25rem 0.5rem;
  border: 1px solid transparent;
  border-radius: ${(props) => {
    if (props.borderRadius) {
      return `${props.borderRadius}px`;
    } else if (props.theme?.borderRadius) {
      return `${props.theme.borderRadius}px`;
    }
    return '0.25rem';
  }};
  border-color: ${(props) => props.borderColor || 'transparent'};
  border-width: ${(props) => {
    if (props.borderWidth) {
      return `${props.borderWidth}px`;
    }
    return '1px';
  }};
  width: 100px;
  text-align: ${(props) => props.textAlign || 'right'};
  text-transform: ${(props) => props.textTransform || 'none'};
  text-decoration: ${(props) => props.textDecoration || 'none'};
  background-color: ${(props) => props.backgroundColor || 'transparent'};
  
  &:focus {
    outline: none;
    border-color: ${(props) => props.theme?.primaryColor || '#cbd5e0'};
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
    background-color: ${(props) => props.theme?.disabledColor || '#f7fafc'};
  }
`;

const SummaryRowControls = styled.div`
  display: flex;
  align-items: center;
`;

const PricingTableSummaryRow = ({
  label,
  value,
  inputValue,
  valueType,
  format,
  isBold = false,
  isLast = false,
  isFocused = false,
  isReadOnly = false,
  menuItems = [],
  onInputChange,
  onInputFocus,
  onInputBlur,
  canManageColumns = true,
  fontSize,
  fontColor,
  fontStyle,
  fontWeight,
  textAlign,
  textTransform,
  textDecoration,
  backgroundColor,
  borderColor,
  borderWidth,
  borderRadius,
  padding,
  theme
}) => {
  // Show controls regardless of mode, as long as canManageColumns is true
  const showControls = canManageColumns;

  // Get the value type based on format if provided
  let cellValueType = valueType;
  if (!cellValueType) {
    if (format === 'percent') {
      cellValueType = 'percent';
    } else {
      cellValueType = 'amount';
    }
  }

  // Always ensure properly formatted display values
  let displayValue;
  if (isFocused) {
    displayValue = inputValue;
  } else if (isFormattedCorrectly(value, cellValueType)) {
    displayValue = value;
  } else {
    displayValue = formatCellValue(value, cellValueType);
  }

  const renderValueComponent = () => {
    if (showControls) {
      return (
        <SummaryInput
          type="text"
          value={displayValue}
          onChange={(e) => onInputChange(e.target.value)}
          onFocus={onInputFocus}
          onBlur={onInputBlur}
          disabled={isReadOnly}
          fontSize={fontSize}
          fontColor={fontColor}
          fontStyle={fontStyle}
          fontWeight={fontWeight}
          textAlign={textAlign}
          textTransform={textTransform}
          textDecoration={textDecoration}
          backgroundColor={backgroundColor}
          borderColor={borderColor}
          borderWidth={borderWidth}
          borderRadius={borderRadius}
          theme={theme}
        />
      );
    }

    return (
      <SummaryValue
        isBold={isBold}
        fontSize={fontSize}
        fontColor={fontColor}
        fontStyle={fontStyle}
        fontWeight={fontWeight}
        textAlign={textAlign}
        textTransform={textTransform}
        textDecoration={textDecoration}
        padding={padding}
        theme={theme}
      >
        {displayValue}
      </SummaryValue>
    );
  };

  return (
    <SummaryRow
      isLast={isLast}
      canManageColumns={canManageColumns}
      backgroundColor={backgroundColor}
      borderColor={borderColor}
      theme={theme}
    >
      <SummaryLabel
        isBold={isBold}
        fontSize={fontSize}
        fontColor={fontColor}
        fontStyle={fontStyle}
        fontWeight={fontWeight}
        textAlign={textAlign}
        textTransform={textTransform}
        textDecoration={textDecoration}
        padding={padding}
        theme={theme}
      >
        {label}
      </SummaryLabel>
      <SummaryRowControls>
        {renderValueComponent()}

        {menuItems.length > 0 && showControls && (
          <DropdownMenu
            triggerElement={(
              <button
                className="pricing-table-dropdown-button"
                style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'none', marginLeft: '0.5rem' }}
              >
                <Icon
                  icon={ThemeIcons.misc.chevronDown}
                  color={theme?.fontColorSecondary || 'black'}
                  hoverColor={theme?.primaryColor}
                />
              </button>
            )}
            menuItems={menuItems}
            theme={theme}
            isSummaryView
          />
        )}
      </SummaryRowControls>
    </SummaryRow>
  );
};

export default PricingTableSummaryRow;

PricingTableSummaryRow.propTypes = {
  label: PropTypes.string,
  value: PropTypes.any,
  inputValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  valueType: PropTypes.string,
  format: PropTypes.string,
  isBold: PropTypes.bool,
  isLast: PropTypes.bool,
  isFocused: PropTypes.bool,
  isReadOnly: PropTypes.bool,
  menuItems: PropTypes.array,
  onInputChange: PropTypes.func,
  onInputFocus: PropTypes.func,
  onInputBlur: PropTypes.func,
  canManageColumns: PropTypes.bool,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  fontColor: PropTypes.string,
  fontStyle: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  textAlign: PropTypes.string,
  textTransform: PropTypes.string,
  textDecoration: PropTypes.string,
  backgroundColor: PropTypes.string,
  borderColor: PropTypes.string,
  borderWidth: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  borderRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  padding: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  theme: PropTypes.object
};

